import {
	type Connection,
	type Edge,
	type EdgeChange,
	type Node,
	type NodeChange,
	addEdge,
	applyEdgeChanges,
	applyNodeChanges,
} from "@xyflow/react";
import { useCallback, useState } from "react";
import type { StepNodeData } from "../lib/yaml-serializer.js";

let nodeCounter = 0;

export function useCanvasState() {
	const [nodes, setNodes] = useState<Node<StepNodeData>[]>([]);
	const [edges, setEdges] = useState<Edge[]>([]);
	const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);

	const onNodesChange = useCallback(
		(changes: NodeChange[]) =>
			setNodes((nds) => applyNodeChanges(changes, nds) as Node<StepNodeData>[]),
		[],
	);

	const onEdgesChange = useCallback(
		(changes: EdgeChange[]) => setEdges((eds) => applyEdgeChanges(changes, eds)),
		[],
	);

	const onConnect = useCallback(
		(connection: Connection) => setEdges((eds) => addEdge(connection, eds)),
		[],
	);

	const addNode = useCallback((stepType: string, position: { x: number; y: number }) => {
		const id = `node-${++nodeCounter}`;
		const stepId = `${stepType}-${nodeCounter}`;
		const newNode: Node<StepNodeData> = {
			id,
			type: "stepNode",
			position,
			data: { stepId, stepType, config: {} },
		};
		setNodes((nds) => [...nds, newNode]);
		return id;
	}, []);

	const removeNode = useCallback(
		(nodeId: string) => {
			setNodes((nds) => nds.filter((n) => n.id !== nodeId));
			setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
			if (selectedNodeId === nodeId) setSelectedNodeId(null);
		},
		[selectedNodeId],
	);

	const updateNodeData = useCallback((nodeId: string, data: Partial<StepNodeData>) => {
		setNodes((nds) =>
			nds.map((n) => (n.id === nodeId ? { ...n, data: { ...n.data, ...data } } : n)),
		);
	}, []);

	return {
		nodes,
		edges,
		selectedNodeId,
		setNodes,
		setEdges,
		setSelectedNodeId,
		onNodesChange,
		onEdgesChange,
		onConnect,
		addNode,
		removeNode,
		updateNodeData,
	};
}
